import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createHousingUnit,
  updateHousingUnit,
} from "../../lib/api-modules/housing-units.js";
import { listAccounts } from "../../lib/api-modules/accounts.js";
import {
  OWNERSHIP_TYPE_LABELS,
  type CreateHousingUnitInput,
  type FlipTaxBasis,
  type HousingUnit,
  type OwnershipType,
  type ValuationSource,
} from "../../lib/api-modules/types.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <HousingUnitForm> — create/edit a housing unit (Phase 5 Session 4).
 *
 * Conditional sections per `ownership_type`:
 *   - **coop**: shares_held + total_shares_outstanding (required by the
 *     server's coherence check) + flip-tax fields + underlying-mortgage
 *     share. NYC-specific UX language ("your share of the building's
 *     mortgage") to match how co-op letters phrase it.
 *   - **condo / SF / townhouse / multi-family**: standard purchase +
 *     valuation fields, no per-share math.
 *
 * Linked accounts: multi-select from existing accounts where
 * `account_type` is mortgage / second_mortgage / heloc / hoa_or_coop.
 * Powers the Amortization tab + lets `<AccountForm>` HOA balance
 * surface in the unit drilldown later.
 */

const OWNERSHIP_OPTIONS: ReadonlyArray<{
  value: OwnershipType;
  label: string;
}> = (Object.keys(OWNERSHIP_TYPE_LABELS) as OwnershipType[]).map((v) => ({
  value: v,
  label: OWNERSHIP_TYPE_LABELS[v],
}));

const VALUATION_SOURCE_OPTIONS: ReadonlyArray<{
  value: ValuationSource;
  label: string;
}> = [
  { value: "manual", label: "Manual entry" },
  { value: "appraisal", label: "Appraisal" },
  { value: "comp_analysis", label: "Comp analysis" },
  { value: "zillow", label: "Zillow (manual lookup)" },
  { value: "redfin", label: "Redfin (manual lookup)" },
];

const FLIP_TAX_BASIS_OPTIONS: ReadonlyArray<{
  value: FlipTaxBasis;
  label: string;
}> = [
  { value: "sale_price", label: "Sale price" },
  { value: "profit", label: "Profit" },
  { value: "shares", label: "Per share" },
  { value: "tiered", label: "Tiered" },
];

// Mortgage-shaped account types that meaningfully participate in unit
// amortization + refinance modeling. `hoa_or_coop` isn't a real account
// type in Phase 3's enum — HOA/co-op fees are paid from a checking
// account, not held in a dedicated account row.
const LINKED_ACCOUNT_TYPES = new Set([
  "mortgage",
  "second_mortgage",
  "heloc",
]);

interface HousingUnitFormProps {
  readonly mode: "create" | "edit";
  readonly unit?: HousingUnit;
  readonly onClose: () => void;
}

export function HousingUnitForm({ mode, unit, onClose }: HousingUnitFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const [ownershipType, setOwnershipType] = useState<OwnershipType>(
    unit?.ownership_type ?? "coop",
  );
  const [isPrimaryResidence, setIsPrimaryResidence] = useState<boolean>(
    unit?.is_primary_residence ?? true,
  );
  const [address, setAddress] = useState(unit?.address ?? "");
  const [unitDesignation, setUnitDesignation] = useState(
    unit?.unit_designation ?? "",
  );
  const [city, setCity] = useState(unit?.city ?? "New York");
  const [state, setState] = useState(unit?.state ?? "NY");
  const [zip, setZip] = useState(unit?.zip ?? "");

  const [purchaseDate, setPurchaseDate] = useState(unit?.purchase_date ?? "");
  const [purchasePriceDollars, setPurchasePriceDollars] = useState(
    unit?.purchase_price_cents !== undefined
      ? (unit.purchase_price_cents / 100).toString()
      : "",
  );

  const [currentValueDollars, setCurrentValueDollars] = useState(
    unit?.current_value_cents !== undefined
      ? (unit.current_value_cents / 100).toString()
      : "",
  );
  const [currentValueAsOf, setCurrentValueAsOf] = useState(
    unit?.current_value_as_of ?? "",
  );
  const [currentValueSource, setCurrentValueSource] = useState<ValuationSource>(
    unit?.current_value_source ?? "manual",
  );

  const [sharesHeld, setSharesHeld] = useState(
    unit?.shares_held !== undefined ? unit.shares_held.toString() : "",
  );
  const [totalShares, setTotalShares] = useState(
    unit?.total_shares_outstanding !== undefined
      ? unit.total_shares_outstanding.toString()
      : "",
  );
  const [flipTaxPct, setFlipTaxPct] = useState(
    unit?.flip_tax_pct !== undefined ? unit.flip_tax_pct.toString() : "",
  );
  const [flipTaxBasis, setFlipTaxBasis] = useState<FlipTaxBasis | "">(
    unit?.flip_tax_basis ?? "",
  );
  const [underlyingMortgageShareDollars, setUnderlyingMortgageShareDollars] =
    useState(
      unit?.underlying_mortgage_share_cents !== undefined
        ? (unit.underlying_mortgage_share_cents / 100).toString()
        : "",
    );

  const [squareFeet, setSquareFeet] = useState(
    unit?.square_feet !== undefined ? unit.square_feet.toString() : "",
  );
  const [yearBuilt, setYearBuilt] = useState(
    unit?.year_built !== undefined ? unit.year_built.toString() : "",
  );

  const [linkedAccountIds, setLinkedAccountIds] = useState<string[]>(
    unit?.linked_account_ids ?? [],
  );

  const [notes, setNotes] = useState(unit?.notes ?? "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Fetch accounts list once for the linked-accounts picker. Filters to
  // mortgage-shaped account types (the only ones meaningful for housing
  // amortization / HOA roll-up).
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => listAccounts(),
  });
  const eligibleAccounts = useMemo(
    () =>
      (accountsQuery.data?.accounts ?? []).filter((a) =>
        LINKED_ACCOUNT_TYPES.has(a.account_type),
      ),
    [accountsQuery.data],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmed = address.trim();
      if (!trimmed) throw new Error("Address is required");

      const purchasePrice = parseDollars(purchasePriceDollars);
      const currentValue = parseDollars(currentValueDollars);
      const underlying = parseDollars(underlyingMortgageShareDollars);
      const sharesH = parseFloatOrUndefined(sharesHeld);
      const totalS = parseFloatOrUndefined(totalShares);

      const baseInput: CreateHousingUnitInput = {
        ownership_type: ownershipType,
        is_primary_residence: isPrimaryResidence,
        address: trimmed,
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip: zip.trim(),
      };
      if (unitDesignation.trim()) baseInput.unit_designation = unitDesignation.trim();
      if (purchaseDate) baseInput.purchase_date = purchaseDate;
      if (purchasePrice !== undefined)
        baseInput.purchase_price_cents = purchasePrice;
      if (currentValue !== undefined) {
        baseInput.current_value_cents = currentValue;
        if (currentValueAsOf) baseInput.current_value_as_of = currentValueAsOf;
        baseInput.current_value_source = currentValueSource;
      }
      if (sharesH !== undefined) baseInput.shares_held = sharesH;
      if (totalS !== undefined) baseInput.total_shares_outstanding = totalS;
      const ftPct = parseFloatOrUndefined(flipTaxPct);
      if (ftPct !== undefined) baseInput.flip_tax_pct = ftPct;
      if (flipTaxBasis) baseInput.flip_tax_basis = flipTaxBasis;
      if (underlying !== undefined)
        baseInput.underlying_mortgage_share_cents = underlying;
      const sqft = parseFloatOrUndefined(squareFeet);
      if (sqft !== undefined) baseInput.square_feet = sqft;
      const yb = parseFloatOrUndefined(yearBuilt);
      if (yb !== undefined) baseInput.year_built = Math.round(yb);
      if (linkedAccountIds.length > 0)
        baseInput.linked_account_ids = linkedAccountIds;
      if (notes.trim()) baseInput.notes = notes.trim();

      if (isEdit && unit) {
        return updateHousingUnit(unit.unit_id, baseInput);
      }
      return createHousingUnit(baseInput);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["housing-units"] });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't save housing unit. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (!address.trim()) {
      setInlineError("Address is required.");
      return;
    }
    mutation.mutate();
  }

  const isCoop = ownershipType === "coop";

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit housing unit" : "Add housing unit"}
      className="flex flex-col gap-4 rounded-md border border-zinc-200 bg-zinc-50/40 p-4 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {isEdit ? "Edit housing unit" : "Add housing unit"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">Ownership type</label>
          <select
            className={inputClass}
            value={ownershipType}
            onChange={(e) => setOwnershipType(e.target.value as OwnershipType)}
            disabled={mutation.isPending}
          >
            {OWNERSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end pb-1.5">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={isPrimaryResidence}
              onChange={(e) => setIsPrimaryResidence(e.target.checked)}
              disabled={mutation.isPending}
            />
            Primary residence (Schedule A eligible)
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium">Address</label>
          <input
            className={inputClass}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St"
            disabled={mutation.isPending}
            autoFocus={!isEdit}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Unit / apt <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            className={inputClass}
            value={unitDesignation}
            onChange={(e) => setUnitDesignation(e.target.value)}
            placeholder="3B"
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">City</label>
          <input
            className={inputClass}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">State</label>
          <input
            className={inputClass}
            value={state}
            onChange={(e) => setState(e.target.value)}
            maxLength={2}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">ZIP</label>
          <input
            className={inputClass}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">
            Purchase date <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="date"
            className={inputClass}
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Purchase price ($){" "}
            <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="number"
            className={inputClass}
            value={purchasePriceDollars}
            onChange={(e) => setPurchasePriceDollars(e.target.value)}
            placeholder="850000"
            disabled={mutation.isPending}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 rounded-md border border-zinc-200 p-3 dark:border-zinc-800 sm:grid-cols-3">
        <header className="text-xs font-medium sm:col-span-3">
          Current valuation{" "}
          <span className="text-zinc-500">
            (manual in v1; Phase 11 lands automated price feeds)
          </span>
        </header>
        <div>
          <label className="mb-1 block text-xs font-medium">Current value ($)</label>
          <input
            type="number"
            className={inputClass}
            value={currentValueDollars}
            onChange={(e) => setCurrentValueDollars(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">As of</label>
          <input
            type="date"
            className={inputClass}
            value={currentValueAsOf}
            onChange={(e) => setCurrentValueAsOf(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Source</label>
          <select
            className={inputClass}
            value={currentValueSource}
            onChange={(e) =>
              setCurrentValueSource(e.target.value as ValuationSource)
            }
            disabled={mutation.isPending}
          >
            {VALUATION_SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {isCoop && (
        <section className="grid grid-cols-1 gap-3 rounded-md border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-900 dark:bg-amber-950/40 sm:grid-cols-2">
          <header className="text-xs font-medium sm:col-span-2">
            Co-op specifics{" "}
            <span className="text-zinc-500">
              (shares + total are required for per-share allocation math)
            </span>
          </header>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Shares you hold
            </label>
            <input
              type="number"
              className={inputClass}
              value={sharesHeld}
              onChange={(e) => setSharesHeld(e.target.value)}
              placeholder="200"
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Total shares outstanding
            </label>
            <input
              type="number"
              className={inputClass}
              value={totalShares}
              onChange={(e) => setTotalShares(e.target.value)}
              placeholder="10000"
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Flip tax %{" "}
              <span className="text-zinc-500">(building's policy)</span>
            </label>
            <input
              type="number"
              step="0.01"
              className={inputClass}
              value={flipTaxPct}
              onChange={(e) => setFlipTaxPct(e.target.value)}
              placeholder="2.0"
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Flip tax basis
            </label>
            <select
              className={inputClass}
              value={flipTaxBasis}
              onChange={(e) =>
                setFlipTaxBasis(e.target.value as FlipTaxBasis | "")
              }
              disabled={mutation.isPending}
            >
              <option value="">—</option>
              {FLIP_TAX_BASIS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium">
              Your share of the building's underlying mortgage ($)
            </label>
            <input
              type="number"
              className={inputClass}
              value={underlyingMortgageShareDollars}
              onChange={(e) =>
                setUnderlyingMortgageShareDollars(e.target.value)
              }
              placeholder="125000"
              disabled={mutation.isPending}
            />
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">
            Square feet <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="number"
            className={inputClass}
            value={squareFeet}
            onChange={(e) => setSquareFeet(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Year built <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            type="number"
            className={inputClass}
            value={yearBuilt}
            onChange={(e) => setYearBuilt(e.target.value)}
            disabled={mutation.isPending}
          />
        </div>
      </section>

      <section>
        <label className="mb-1 block text-xs font-medium">
          Linked accounts{" "}
          <span className="text-zinc-500">
            (mortgage, second mortgage, HELOC, HOA / co-op)
          </span>
        </label>
        {accountsQuery.isLoading ? (
          <p className="text-xs text-zinc-500">Loading accounts…</p>
        ) : eligibleAccounts.length === 0 ? (
          <p className="text-xs text-zinc-500">
            No mortgage / HELOC / HOA accounts yet — add one at{" "}
            <a href="/accounts" className="text-brand-600 hover:underline">
              /accounts
            </a>
            .
          </p>
        ) : (
          <ul className="flex flex-col gap-1 rounded-md border border-zinc-200 p-2 dark:border-zinc-800">
            {eligibleAccounts.map((a) => (
              <li key={a.account_id}>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={linkedAccountIds.includes(a.account_id)}
                    onChange={(e) => {
                      setLinkedAccountIds((prev) =>
                        e.target.checked
                          ? [...prev, a.account_id]
                          : prev.filter((id) => id !== a.account_id),
                      );
                    }}
                    disabled={mutation.isPending}
                  />
                  <span className="font-mono text-xs">{a.account_type}</span>
                  <span>{a.name}</span>
                  {a.institution && (
                    <span className="text-zinc-500">· {a.institution}</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Notes <span className="text-zinc-500">(optional)</span>
        </label>
        <textarea
          className={inputClass}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          maxLength={2000}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : isEdit ? "Save unit" : "Add unit"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function parseDollars(s: string): number | undefined {
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}
function parseFloatOrUndefined(s: string): number | undefined {
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return n;
}
